import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AreaChart,
    Area,
    PieChart,
    Pie,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell
} from 'recharts';
import { AlertTriangle, TrendingDown, TrendingUp, Clock, Users } from 'lucide-react';
import { apiClient, SiteRiskTrendsData } from '../services/api';

// Brand color palette
const COLORS = {
    primary: '#69BE28',
    amber: '#F59E0B',
    red: '#EF4444',
    blue: '#3B82F6',
    purple: '#8B5CF6',
    cyan: '#06B6D4',
    // Document-specific colors for Expiry Forecast
    licence: '#22C55E',    // Green - Primary document
    cpc: '#3B82F6',        // Blue - Professional qualification
    tacho: '#6366F1',      // Indigo - Tachograph card
    consent: '#8B5CF6',    // Purple - Legal/administrative
};

// Risk level to color mapping
const RISK_COLORS = {
    low: COLORS.primary,
    medium: COLORS.amber,
    high: COLORS.red,
};

// Custom tooltip for consistent styling
interface CustomTooltipProps {
    active?: boolean;
    payload?: Array<{ name: string; value: number; color: string; payload?: Record<string, unknown> }>;
    label?: string;
    labelFormatter?: (label: string) => string;
}

const CustomTooltip = ({ active, payload, label, labelFormatter }: CustomTooltipProps) => {
    if (!active || !payload || !payload.length) return null;

    return (
        <div className="bg-white rounded-lg shadow-lg border border-gray-100 p-3 min-w-[140px]">
            <p className="text-xs font-medium text-gray-500 mb-2 border-b border-gray-100 pb-1">
                {labelFormatter ? labelFormatter(label as string) : label}
            </p>
            {payload.map((entry, index) => (
                <div key={index} className="flex items-center justify-between gap-4 text-sm">
                    <span className="flex items-center gap-1.5">
                        <span
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: entry.color }}
                        />
                        <span className="text-gray-600">{entry.name}</span>
                    </span>
                    <span className="font-semibold text-gray-900">
                        {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}
                    </span>
                </div>
            ))}
        </div>
    );
};

// "Not enough data" placeholder component
const NotEnoughData = ({ message }: { message: string }) => (
    <div className="h-[140px] flex flex-col items-center justify-center text-gray-400 bg-gray-50/50 rounded-lg border border-dashed border-gray-200">
        <Clock className="w-8 h-8 mb-2 opacity-50" />
        <span className="text-sm">{message}</span>
        <span className="text-xs mt-1">Check back tomorrow</span>
    </div>
);

interface SiteTrendsTabProps {
    refreshKey?: number;
}

const SiteTrendsTab: React.FC<SiteTrendsTabProps> = ({ refreshKey }) => {
    const navigate = useNavigate();
    const [data, setData] = useState<SiteRiskTrendsData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [days, setDays] = useState(30);

    const loadTrends = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await apiClient.getSiteRiskTrends(days);
            setData(response.data);
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load trends data';
            setError(errorMessage);
            console.error('Error loading trends:', err);
        } finally {
            setIsLoading(false);
        }
    }, [days]);

    useEffect(() => {
        loadTrends();
    }, [loadTrends, refreshKey]);

    const formatDate = (value: string) => {
        const date = new Date(value);
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    };

    const formatDateShort = (value: string) => {
        const date = new Date(value);
        return `${date.getDate()}/${date.getMonth() + 1}`;
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
                <div className="relative">
                    <div className="w-12 h-12 rounded-full border-4 border-gray-200"></div>
                    <div className="absolute top-0 left-0 w-12 h-12 rounded-full border-4 border-transparent border-t-[#69BE28] animate-spin"></div>
                </div>
                <span className="text-sm text-gray-500">Loading trends...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                </div>
                <span className="text-sm text-red-600">{error}</span>
                <button
                    onClick={loadTrends}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800"
                >
                    Try again
                </button>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-3 text-gray-400">
                <Users className="w-12 h-12" />
                <span className="text-sm">No trend data available</span>
            </div>
        );
    }

    // Prepare risk distribution data - each site as individual segment
    const pieData = data.riskDistribution.sites.map(site => ({
        siteId: site.siteId,
        name: site.siteName,
        value: 1, // Each site is equal weight in pie
        color: RISK_COLORS[site.riskLevel],
        riskLevel: site.riskLevel,
        score: site.score,
    }));

    const totalSites = pieData.length;
    const hasEnoughTrendData = data.fleetTrend.length >= 2;

    // Calculate expiry totals per time period
    const totalExpired = data.expiryForecast.licence.expired +
                         data.expiryForecast.cpc.expired +
                         data.expiryForecast.tacho.expired +
                         data.expiryForecast.consent.expired;

    const totalExpiring30 = data.expiryForecast.licence.next30 +
                            data.expiryForecast.cpc.next30 +
                            data.expiryForecast.tacho.next30 +
                            data.expiryForecast.consent.next30;

    const totalExpiring60 = data.expiryForecast.licence.next60 +
                            data.expiryForecast.cpc.next60 +
                            data.expiryForecast.tacho.next60 +
                            data.expiryForecast.consent.next60;

    const totalExpiring90 = data.expiryForecast.licence.next90 +
                            data.expiryForecast.cpc.next90 +
                            data.expiryForecast.tacho.next90 +
                            data.expiryForecast.consent.next90;

    // Get fleet trend direction
    const fleetTrendDirection = hasEnoughTrendData
        ? data.fleetTrend[data.fleetTrend.length - 1].avgScore - data.fleetTrend[0].avgScore
        : 0;

    const currentAvgScore = data.fleetTrend.length > 0
        ? data.fleetTrend[data.fleetTrend.length - 1].avgScore
        : 0;

    // Check if site comparison has enough data
    const hasSiteComparisonData = data.siteComparison.top5.some(site => site.history.length >= 2);

    return (
        <div className="space-y-4">
            {/* Period Selector */}
            <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Period:</span>
                <select
                    value={days}
                    onChange={(e) => setDays(Number(e.target.value))}
                    className="text-sm font-medium bg-white border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#69BE28]/20 focus:border-[#69BE28] transition-colors cursor-pointer"
                >
                    <option value={30}>Last 30 days</option>
                    <option value={60}>Last 60 days</option>
                    <option value={90}>Last 90 days</option>
                </select>
            </div>

            {/* 2x2 Grid of Charts - Ordered by priority: Urgent → Overview → Context → Detail */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 1. Expiry Forecast - Most urgent/actionable (top-left) */}
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h4 className="text-sm font-semibold text-gray-800">Expiry Forecast</h4>
                            <p className="text-xs text-gray-500 mt-0.5">Click any number to view drivers</p>
                        </div>
                        {(totalExpired > 0 || totalExpiring30 > 0) && (
                            <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700">
                                <AlertTriangle className="w-3 h-3" />
                                {totalExpired + totalExpiring30} urgent
                            </div>
                        )}
                    </div>

                    {/* Clean Table Design */}
                    <div className="space-y-1">
                        {/* Header */}
                        <div className="grid grid-cols-5 gap-2 text-[10px] uppercase tracking-wider font-medium text-gray-400 pb-2">
                            <div></div>
                            <div className="text-center">Expired</div>
                            <div className="text-center">30 days</div>
                            <div className="text-center">60 days</div>
                            <div className="text-center">90 days</div>
                        </div>

                        {/* Document Rows */}
                        {[
                            { name: 'Licence', color: COLORS.licence, filterPrefix: 'licence', data: data.expiryForecast.licence },
                            { name: 'CPC', color: COLORS.cpc, filterPrefix: 'cpc', data: data.expiryForecast.cpc },
                            { name: 'Tacho', color: COLORS.tacho, filterPrefix: 'tacho', data: data.expiryForecast.tacho },
                            { name: 'Consent', color: COLORS.consent, filterPrefix: 'consent', data: data.expiryForecast.consent },
                        ].map((doc) => {
                            const cells = [
                                { value: doc.data.expired, filter: `${doc.filterPrefix}_expired`, isExpired: true },
                                { value: doc.data.next30, filter: `${doc.filterPrefix}_expiring`, minDays: 0, maxDays: 30 },
                                { value: doc.data.next60, filter: `${doc.filterPrefix}_expiring`, minDays: 30, maxDays: 60 },
                                { value: doc.data.next90, filter: `${doc.filterPrefix}_expiring`, minDays: 60, maxDays: 90 },
                            ];

                            return (
                                <div key={doc.name} className="grid grid-cols-5 gap-2 items-center py-1.5 border-t border-gray-50">
                                    {/* Document Label */}
                                    <div className="flex items-center gap-2">
                                        <span className="h-4 w-[3px] rounded-full flex-shrink-0" style={{ backgroundColor: doc.color }} aria-hidden="true"></span>
                                        <span className="text-xs font-medium text-gray-700">{doc.name}</span>
                                    </div>

                                    {/* Data Cells */}
                                    {cells.map((cell, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => cell.value > 0 && navigate(
                                                cell.isExpired
                                                    ? `/drivers?show=${cell.filter}`
                                                    : `/drivers?show=${cell.filter}&minDays=${cell.minDays}&maxDays=${cell.maxDays}`
                                            )}
                                            className={`text-center py-1 rounded-md transition-all ${
                                                cell.value > 0
                                                    ? 'cursor-pointer hover:bg-gray-50'
                                                    : ''
                                            }`}
                                        >
                                            <span className={`text-sm font-semibold ${
                                                cell.value > 0
                                                    ? cell.isExpired
                                                        ? 'text-red-700'
                                                        : idx === 1
                                                            ? 'text-red-600'
                                                            : idx === 2
                                                                ? 'text-amber-600'
                                                                : 'text-emerald-600'
                                                    : 'text-gray-300'
                                            }`}>
                                                {cell.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}

                        {/* Totals Row */}
                        <div className="grid grid-cols-5 gap-2 items-center pt-2 mt-1 border-t border-gray-200">
                            <div className="text-xs font-semibold text-gray-500">Total</div>
                            {[
                                { value: totalExpired, url: '/drivers?show=all_expired', color: 'text-red-700', bg: 'bg-red-50 hover:bg-red-100' },
                                { value: totalExpiring30, url: '/drivers?show=all_expiring&minDays=0&maxDays=30', color: 'text-red-600', bg: 'bg-red-50 hover:bg-red-100' },
                                { value: totalExpiring60, url: '/drivers?show=all_expiring&minDays=30&maxDays=60', color: 'text-amber-600', bg: 'bg-amber-50 hover:bg-amber-100' },
                                { value: totalExpiring90, url: '/drivers?show=all_expiring&minDays=60&maxDays=90', color: 'text-emerald-600', bg: 'bg-emerald-50 hover:bg-emerald-100' },
                            ].map((cell, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => cell.value > 0 && navigate(cell.url)}
                                    className={`text-center py-1 rounded-md transition-all ${
                                        cell.value > 0
                                            ? `cursor-pointer ${cell.bg}`
                                            : ''
                                    }`}
                                >
                                    <span className={`text-sm font-bold ${cell.value > 0 ? cell.color : 'text-gray-300'}`}>
                                        {cell.value}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* All clear state */}
                    {totalExpired === 0 && totalExpiring30 === 0 && totalExpiring60 === 0 && totalExpiring90 === 0 && (
                        <div className="mt-4 text-center py-3 bg-green-50 rounded-lg">
                            <p className="text-xs text-green-600 font-medium">✓ No documents expired or expiring in the next 90 days</p>
                        </div>
                    )}
                </div>

                {/* 2. Risk Distribution - Current state overview (top-right) */}
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h4 className="text-sm font-semibold text-gray-800">Risk Distribution</h4>
                            <p className="text-2xl font-bold text-gray-900 mt-1">
                                {totalSites}
                                <span className="text-xs font-normal text-gray-400 ml-1">sites</span>
                            </p>
                        </div>
                    </div>
                    {pieData.length > 0 ? (
                        <div className="flex items-center min-h-[200px]">
                            <ResponsiveContainer width="50%" height={180}>
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={45}
                                        outerRadius={75}
                                        paddingAngle={2}
                                        dataKey="value"
                                        strokeWidth={2}
                                        stroke="#fff"
                                        cursor="pointer"
                                        onClick={(_, index) => {
                                            if (index !== undefined && pieData[index]) {
                                                navigate(`/drivers?site=${pieData[index].siteId}`);
                                            }
                                        }}
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={entry.color}
                                                className="cursor-pointer hover:opacity-80 transition-opacity"
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        content={({ active, payload }) => {
                                            if (!active || !payload?.length) return null;
                                            const d = payload[0].payload;
                                            return (
                                                <div className="bg-white rounded-lg shadow-lg border border-gray-100 px-3 py-2">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                                                        <span className="text-sm font-medium text-gray-800">{d.name}</span>
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        Score: <span className="font-semibold text-gray-700">{d.score.toFixed(1)}</span>
                                                        <span className="ml-2 capitalize">({d.riskLevel} risk)</span>
                                                    </div>
                                                    <div className="text-[10px] text-gray-400 mt-1 pt-1 border-t border-gray-100">
                                                        Click to view drivers
                                                    </div>
                                                </div>
                                            );
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="flex flex-col gap-3 flex-1 text-sm justify-center">
                                {/* Summary by risk level - clickable */}
                                <div
                                    onClick={() => data.riskDistribution.low > 0 && navigate('/drivers?risk=low')}
                                    className={`flex items-center justify-between py-2 border-b border-gray-100 rounded transition-all ${
                                        data.riskDistribution.low > 0 ? 'cursor-pointer hover:bg-gray-50' : ''
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="h-4 w-[3px] rounded-full flex-shrink-0" style={{ backgroundColor: COLORS.primary }} aria-hidden="true" />
                                        <span className="text-gray-700">Low Risk</span>
                                    </div>
                                    <span className={`font-bold text-lg ${data.riskDistribution.low > 0 ? 'text-gray-900' : 'text-gray-300'}`}>{data.riskDistribution.low}</span>
                                </div>
                                <div
                                    onClick={() => data.riskDistribution.medium > 0 && navigate('/drivers?risk=medium')}
                                    className={`flex items-center justify-between py-2 border-b border-gray-100 rounded transition-all ${
                                        data.riskDistribution.medium > 0 ? 'cursor-pointer hover:bg-gray-50' : ''
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="h-4 w-[3px] rounded-full flex-shrink-0" style={{ backgroundColor: COLORS.amber }} aria-hidden="true" />
                                        <span className="text-gray-700">Medium Risk</span>
                                    </div>
                                    <span className={`font-bold text-lg ${data.riskDistribution.medium > 0 ? 'text-gray-900' : 'text-gray-300'}`}>{data.riskDistribution.medium}</span>
                                </div>
                                <div
                                    onClick={() => data.riskDistribution.high > 0 && navigate('/drivers?risk=high')}
                                    className={`flex items-center justify-between py-2 rounded transition-all ${
                                        data.riskDistribution.high > 0 ? 'cursor-pointer hover:bg-gray-50' : ''
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="h-4 w-[3px] rounded-full flex-shrink-0" style={{ backgroundColor: COLORS.red }} aria-hidden="true" />
                                        <span className="text-gray-700">High Risk</span>
                                    </div>
                                    <span className={`font-bold text-lg ${data.riskDistribution.high > 0 ? 'text-gray-900' : 'text-gray-300'}`}>{data.riskDistribution.high}</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm bg-gray-50 rounded-lg">
                            No sites to display
                        </div>
                    )}
                </div>

                {/* 3. Driver Risk Trend - Context over time (bottom-left) */}
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h4 className="text-sm font-semibold text-gray-800">Driver Risk Trend</h4>
                            <p className="text-xs text-gray-500 mt-0.5">Average driver score over time</p>
                            {currentAvgScore > 0 && (
                                <p className="text-2xl font-bold text-gray-900 mt-1">
                                    {currentAvgScore.toFixed(1)}
                                    <span className="text-xs font-normal text-gray-400 ml-1">avg score</span>
                                </p>
                            )}
                        </div>
                        {hasEnoughTrendData && fleetTrendDirection !== 0 && (
                            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                fleetTrendDirection < 0
                                    ? 'bg-green-50 text-green-700'
                                    : 'bg-red-50 text-red-700'
                            }`}>
                                {fleetTrendDirection < 0 ? (
                                    <><TrendingDown className="w-3 h-3" /> Improving</>
                                ) : (
                                    <><TrendingUp className="w-3 h-3" /> Worsening</>
                                )}
                            </div>
                        )}
                    </div>
                    {hasEnoughTrendData ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <AreaChart data={data.fleetTrend}>
                                <defs>
                                    <linearGradient id="fleetGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.3} />
                                        <stop offset="100%" stopColor={COLORS.primary} stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 10, fill: '#94a3b8' }}
                                    tickFormatter={formatDateShort}
                                    axisLine={{ stroke: '#e2e8f0' }}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={{ fontSize: 10, fill: '#94a3b8' }}
                                    domain={[0, 'auto']}
                                    axisLine={false}
                                    tickLine={false}
                                    width={30}
                                />
                                <Tooltip content={<CustomTooltip labelFormatter={formatDate} />} />
                                <Area
                                    type="monotone"
                                    dataKey="avgScore"
                                    name="Avg Score"
                                    stroke={COLORS.primary}
                                    strokeWidth={2.5}
                                    fill="url(#fleetGradient)"
                                    dot={{ fill: COLORS.primary, strokeWidth: 0, r: 3 }}
                                    activeDot={{ fill: COLORS.primary, stroke: '#fff', strokeWidth: 2, r: 6 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <NotEnoughData message="Need more data for trend" />
                    )}
                </div>

                {/* 4. Site Comparison - Detailed drill-down (bottom-right) */}
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h4 className="text-sm font-semibold text-gray-800">Site Comparison</h4>
                            <p className="text-xs text-gray-500 mt-0.5">Top performing sites over time</p>
                        </div>
                    </div>
                    {hasSiteComparisonData ? (
                        <>
                            <ResponsiveContainer width="100%" height={220}>
                                <AreaChart>
                                    <defs>
                                        {data.siteComparison.top5.map((site, index) => {
                                            const colors = [COLORS.primary, COLORS.blue, COLORS.purple, COLORS.cyan, COLORS.amber];
                                            return (
                                                <linearGradient key={`gradient-${index}`} id={`siteGradient${index}`} x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor={colors[index % colors.length]} stopOpacity={0.2} />
                                                    <stop offset="100%" stopColor={colors[index % colors.length]} stopOpacity={0} />
                                                </linearGradient>
                                            );
                                        })}
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 9, fill: '#94a3b8' }}
                                        tickFormatter={formatDateShort}
                                        allowDuplicatedCategory={false}
                                        axisLine={{ stroke: '#e2e8f0' }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 9, fill: '#94a3b8' }}
                                        domain={[0, 'auto']}
                                        axisLine={false}
                                        tickLine={false}
                                        width={25}
                                    />
                                    <Tooltip content={<CustomTooltip labelFormatter={formatDate} />} />
                                    {data.siteComparison.top5.map((site, index) => {
                                        const colors = [COLORS.primary, COLORS.blue, COLORS.purple, COLORS.cyan, COLORS.amber];
                                        return (
                                            <Area
                                                key={site.siteId}
                                                data={site.history}
                                                type="monotone"
                                                dataKey="score"
                                                name={site.siteName}
                                                stroke={colors[index % colors.length]}
                                                strokeWidth={2}
                                                fill={`url(#siteGradient${index})`}
                                                dot={{ fill: colors[index % colors.length], strokeWidth: 0, r: 2 }}
                                                activeDot={{ fill: colors[index % colors.length], stroke: '#fff', strokeWidth: 2, r: 4 }}
                                            />
                                        );
                                    })}
                                </AreaChart>
                            </ResponsiveContainer>
                            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 justify-center">
                                {data.siteComparison.top5.map((site, index) => {
                                    const colors = [COLORS.primary, COLORS.blue, COLORS.purple, COLORS.cyan, COLORS.amber];
                                    return (
                                        <div key={site.siteId} className="flex items-center gap-1 text-xs">
                                            <span
                                                className="h-4 w-[3px] rounded-full flex-shrink-0"
                                                style={{ backgroundColor: colors[index % colors.length] }}
                                                aria-hidden="true"
                                            />
                                            <span className="text-gray-600">
                                                {site.siteName.length > 12 ? site.siteName.substring(0, 12) + '...' : site.siteName}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        <NotEnoughData message="Need more data for comparison" />
                    )}
                </div>
            </div>
        </div>
    );
};

export default SiteTrendsTab;
